import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SimpleArrowAccordionV2 } from 'src/components/Accordion/SimpleArrowAccordionV2/SimpleArrowAccordionV2';
import { AccordionItem } from 'src/components/Accordion/models';
import { useCart } from 'src/components/Cart/UseCart';
import { Image } from 'src/components/Image/Image';
import { TextV2 } from 'src/components/TextV2/TextV2';
import closeIcon from 'src/images/icons/close-black.svg';
import { Promotion } from 'src/services/shop/cart';
import { useProgramPromotion } from '../../ProgramPromotion/ProgramPromotion';
import { PromoCodeInput } from '../PromoCodeInput/PromoCodeInput';

import styles from './PromoSummary.scss';

export const PromoSummary: React.FC = () => {
  const { addPromo, checkout: { promo : promoInCart }, removePromo } = useCart();
  const { programPromotion } = useProgramPromotion();
  const { t } = useTranslation();
  const [userAppliedPromotion, setUserAppliedPromotion] = useState<Promotion | null>();

  const hasProgramPromoApplied = promoInCart.applied && promoInCart.applied === promoInCart.program;
  const activePromo = userAppliedPromotion ? userAppliedPromotion : programPromotion;
  const endOfDayOffsetInMilliseconds = 3600000 * 23 + 59 * 60000;

  useEffect(() => {
    if (!programPromotion) return;

    addPromo({
      program: programPromotion.promoCode,
    });
  }, [programPromotion]);

  useEffect(() => {
    if (!userAppliedPromotion) return;

    addPromo({
      applied: userAppliedPromotion.promoCode,
    });
  }, [userAppliedPromotion]);

  const handleOnPromoFound = (promo: Promotion) => {
    setUserAppliedPromotion(promo);
  };

  const removeAppliedPromo = () => {
    removePromo();
    setUserAppliedPromotion(null);
  };

  return (
    <div className={styles.promoSummary}>
      <SimpleArrowAccordionV2
        className={styles.promoSummaryAccordion}
        items={[{
          defaultOpen: true,
          headerClassName: styles.promoSummaryAccordionHeader,
          title: t('promo-code.heading'),
          body: <PromoCodeInput onPromoFound={handleOnPromoFound} />,
        } as AccordionItem]}
      />

      {promoInCart.applied && (
        <div
          className={styles.promoSummaryBox}
          data-test="applied-box"
        >
          {!hasProgramPromoApplied && (
            <button
              aria-label={t('promo-code.remove-promo-code-icon-alt-text')}
              className={styles.promoSummaryCloseButton}
              onClick={removeAppliedPromo}
              data-test="remove-promo-button"
            >
              <Image src={closeIcon} role="presentation" />
            </button>
          )}

          <TextV2
            tagName="h4"
            variant="body-medium"
            value={t('promo-code.title', { code: promoInCart.applied })}
            data-test="title"
          />

          {activePromo && (
            <TextV2
              tagName="p"
              variant="body-small"
              value={
                t('promo-code.description', {
                  date: (new Date(Date.parse(activePromo.endDate) + endOfDayOffsetInMilliseconds)).toLocaleDateString(),
                  description: activePromo.promotionLongDescription,
              })}
              className={styles.promoSummaryDescription}
              data-test="description"
            />
          )}
        </div>
      )}
    </div>
  );
};

PromoSummary.displayName = 'PromoSummary';
